import { debounce } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GLExceptionRegenerate,
  getGLExceptionData,
  getGLExceptionDataCount,
} from '../../../api/journalGLConfig';
import { InfoLabel, Layout, Pagination, SearchInput, Table } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import { usePagination } from '../../../common/hooks/data';
import { routeTitles } from '../../../constants/routes';
import { GLExceptionTableColumns, TrustGLExceptionTableColumns } from '../../../utils/constant';
import { apiErrorHandler, successHandler } from '../../../utils/handlers';

// eslint-disable-next-line react/prop-types
const JournalGLException = ({ reportType }) => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { page, limit, setPage, setLimit } = usePagination();
  const [slStartNumber, setSlStartNumber] = useState(null);
  const [isApplyFilters, setIsApplyFilters] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const debounceAPICall = useCallback(
    debounce((val) => {
      fetchGLExceptionsList('search', val);
    }, 2000),
    [],
  );

  const onSearchInputChange = (event) => {
    const {
      target: { value },
    } = event;
    setSearchText(value);
    debounceAPICall(value);
  };

  const fetchGLExceptionsList = async (type = 'pagination', value = '') => {
    setLoading(true);
    setIsApplyFilters(false);
    let page2 = page;
    let limit2 = limit;
    if (type === 'search') {
      page2 = 1;
      limit2 = 10;
    }

    const requestParams = {
      page: page2,
      limit: limit2,
      searchText: type === 'search' ? value.trim() : searchText.trim(),
      isTrust: reportType === 'trustGLException' ? 1 : 0,
    };

    try {
      const response = await getGLExceptionData(requestParams);
      if (type === 'search') {
        setPage(1);
        setLimit(10);
      }
      const countResponse = await getGLExceptionDataCount({
        searchText: type === 'search' ? value.trim() : searchText.trim(),
        isTrust: reportType === 'trustGLException' ? 1 : 0,
      });
      setTableData(response?.data?.data);
      setSlStartNumber(page * limit - limit);
      setTotalCount(countResponse?.data?.data);
      setLoading(false);
    } catch (err) {
      apiErrorHandler(err);
      setLoading(false);
    }
  };

  const handleRegenerate = async (id) => {
    setLoading(true);
    try {
      await GLExceptionRegenerate({ id });
      successHandler({
        message: {
          message: 'Journal Entry is posted successfully',
          title: 'Success',
        },
      });
    } catch (err) {
      apiErrorHandler(err);
    } finally {
      await fetchGLExceptionsList();
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isApplyFilters) {
      fetchGLExceptionsList();
    }
  }, [page, limit]);
  useEffect(() => {
    setIsApplyFilters(true);
    setLimit(10);
  }, []);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  return (
    <Layout>
      <div className="d-flex j-gl-heading mt-5">
        <InfoLabel
          showIcon
          label={
            reportType === 'trustGLException'
              ? routeTitles.TRUST_GL_EXCEPTION_SCREEN
              : routeTitles.GL_EXCEPTION_SCREEN
          }
        />
        <div className="ml-auto d-flex search-toolbar col-5 p-0">
          <SearchInput
            placeholder="Search Using Contract Number"
            searchText={searchText}
            handleChange={onSearchInputChange}
          />
        </div>
      </div>
      <hr />
      {tableData?.length > 0 ? (
        <>
          <div>
            <Table
              headers={
                reportType === 'trustGLException'
                  ? TrustGLExceptionTableColumns
                  : GLExceptionTableColumns
              }
              bordered
              responsive
            >
              <tbody>
                {tableData?.map((item, index) => (
                  <tr key={index} className="table-body-tr gl-exception-row">
                    <td className="s-no">{slStartNumber + index + 1}</td>
                    <td>{moment(item?.date)?.format('MM/DD/YYYY')}</td>
                    <td className="contract-number">{item?.contractNumber || '-'}</td>
                    <td className="purchaser-id">{item?.purchaserOPI || '-'}</td>
                    <td className="event">{item?.event || '-'}</td>
                    <td className="reason">{item?.reason || '-'}</td>
                    {reportType === 'trustGLException' && (
                      <td className="reason">{item?.glLocationName || '-'}</td>
                    )}
                    <td
                      className="exception-regenrate"
                      onClick={() => {
                        handleRegenerate(item.id);
                      }}
                    >
                      Regenerate
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="pagination-araging-report">
            <Pagination
              totalResults={totalCount}
              page={page}
              limit={limit}
              setPage={(pageNo) => {
                setPage(pageNo);
                setIsApplyFilters(true);
              }}
              setLimit={(rowlimit) => {
                setLimit(rowlimit);
                setIsApplyFilters(true);
              }}
            />
          </div>
        </>
      ) : (
        <div className="no-reports">
          <p className="no-reports-1">No Exceptions Available</p>
        </div>
      )}
    </Layout>
  );
};

export default JournalGLException;
